/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { OffDeviceLinkService } from 'hig/lib/services/off-device-link.service';
import { RedirectJsoParameters, CommonDynamicRedirect, RedirectPlatform, RedirectOptions } from './redirect';

/**
 * Should handle all redirect.asp related scenarios, by parameterizing the URL correctly.
 * Should also own whether the redirect has to happen on the same window, or in a new one.
 */

@Injectable({
    providedIn: 'root'
})
export class RedirectService {
    private logger: Logger;

    constructor(
        loggingService: LoggingService,
        private localeService: LocaleService,
        private offDeviceLinkService: OffDeviceLinkService
    ) {
        this.logger = loggingService.getLogger('gfn/RedirectService');
    }

    // JSO is not needed
    handleUnsupportedAndroidPlatform(): void {
        this.redirectTo(CommonDynamicRedirect.GfnPcUnsupportedDeviceAndroid, {
            replace: true
        });
    }

    handleUnsupportedBrowser(): void {
        this.redirectTo(CommonDynamicRedirect.GfnPcUnsupportedDevice, {
            replace: true,
            jso: {
                Plt: RedirectPlatform.Browser
            }
        });
    }

    redirectTo(page: CommonDynamicRedirect, options?: RedirectOptions): void {
        this.getRedirectUrl(page, options?.jso).subscribe(redirectUrl => {
            this.logger.info('Redirecting to', redirectUrl);
            if (options?.replace) {
                window.location.replace(redirectUrl);
            } else {
                window.location.href = redirectUrl;
            }
        });
    }

    redirectToExternalUrl(externalUrl: string, options?: RedirectOptions) {
        if (options?.replace) {
            window.location.replace(externalUrl);
        } else {
            window.location.href = externalUrl;
        }
    }

    getRedirectUrl(page: CommonDynamicRedirect, jso?: RedirectJsoParameters): Observable<string> {
        return this.localeService.localeChanged.pipe(take(1)).pipe(
            map(localeCode => {
                let url = `${CommonDynamicRedirect.BaseUrl}${localeCode}&page=${page}`;
                if (jso) {
                    const redirectParams = window.btoa(JSON.stringify(jso));
                    url += `&jso=${redirectParams}`;
                }
                return url;
            })
        );
    }

    openRedirectUrl(featureRedirectKW: CommonDynamicRedirect, shortURL?: string, jso?: RedirectJsoParameters): void {
        this.logger.info('Support Url clicked for: ', featureRedirectKW, shortURL);
        const option = shortURL ? { shortUrl: shortURL } : undefined;
        this.getRedirectUrl(featureRedirectKW, jso)
            .pipe(take(1))
            .subscribe((redirectUrl: string) => {
                this.offDeviceLinkService.open(redirectUrl, option);
            });
    }
}
